import React, { useCallback, useMemo, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useHistory } from 'react-router-dom'
import { splitReply } from './protocol'
import { tidyParts } from './tidy'
import { WIDGETS } from './widgets'
import { safeComponents } from './safeMarkdown'
import { blocksCoveredByDrafts } from '../../draftCards'
import './skills.css'

// One widget going wrong never takes the conversation with it
class Safe extends React.Component {
  constructor(props) { super(props); this.state = { broke: false } }

  static getDerivedStateFromError() { return { broke: true } }

  componentDidCatch(error) { if (typeof console !== 'undefined') console.warn('Bailey widget', error) } // eslint-disable-line no-console

  render() {
    if (this.state.broke) return <p className="bw-note">This one couldn’t be shown. Ask Bailey to try again.</p>
    return this.props.children
  }
}

// A stable name for a block, so a widget can remember what was done with it
// (a saved draft) across reopening the conversation
export const blockKey = (raw) => {
  let h = 5381
  const s = String(raw || '')
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0 // eslint-disable-line no-bitwise
  return h.toString(36)
}

// Bailey's reply: its words, with each thing it shows drawn in place.
// `compact` for the desk pet's small chat. `say` sends words back as theirs
// (a tapped answer); only the latest reply gets it. `onNavigate` hears when
// a widget or link opens a page (the desk pet closes its chat).
// `asked` is what they said before this reply, so an email wears the template
// that fits it even when the model didn't name one
// `drafts` are the campaign drafts this reply already shows as cards (from
// the tool result): an email or text block for one of them isn't drawn again
// `after` (the draft cards) is drawn after the words and before the closing
// buttons, so the question about it comes last
const BaileyReply = ({ text, className = 'bly-md', compact = false, surface = 'tab', say = null, onNavigate = null, asked = '', drafts = null, after = null }) => {
  const history = useHistory()
  const heard = useRef(onNavigate)
  heard.current = onNavigate
  // Where a widget or link goes, with anything the page reads on arrival.
  // Made once, so a link isn't replaced mid-click while a reply streams in.
  const go = useCallback((href, state) => {
    if (history) history.push(href, state)
    if (heard.current) heard.current(href)
  }, [history])
  // Pages in Beu open in place, Beu's own addresses in a new tab; other
  // addresses are shown, not linked, and pictures are never loaded
  const components = useMemo(() => safeComponents(go), [go])
  // Short and tappable: no narration, no "go here to open it" beside a
  // draft card, and a closing question gets buttons (tidy.js)
  // and in mini paragraphs with the facts in bold, to scan at a glance
  const parts = tidyParts(splitReply(text), { drafts, shape: true })
  const covered = blocksCoveredByDrafts(parts, drafts)
  // The closing choices, if any, go after `after`
  let tail = parts.length
  while (tail > 0 && parts[tail - 1].kind === 'block' && parts[tail - 1].block && parts[tail - 1].block.show === 'choices') tail -= 1
  const draw = (p, i) => {
    if (p.kind === 'md') {
      return <div key={`md-${i}`} className={className}><ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{p.text}</ReactMarkdown></div>
    }
    if (p.kind === 'pending') {
      return <div key={`pending-${i}`} className="bw-pending" role="status"><span className="bw-pending-bar" aria-hidden /><span>Getting it ready</span></div>
    }
    if (p.kind === 'bad' || covered.has(i)) return null
    const Widget = WIDGETS[p.block.show]
    if (!Widget) return null
    return (
      <Safe key={`w-${i}-${blockKey(p.raw)}`}>
        <Widget block={p.block} id={blockKey(p.raw)} compact={compact} surface={surface} go={go} say={say} asked={asked} latest={!!say} />
      </Safe>
    )
  }
  return (
    <div className={`bw-reply${compact ? ' is-compact' : ''}`}>
      {parts.slice(0, tail).map((p, i) => draw(p, i))}
      {after}
      {parts.slice(tail).map((p, i) => draw(p, tail + i))}
    </div>
  )
}

export default BaileyReply
